import { Dec, type Decimal } from '../../shared/decimal'
import { legacyWindow } from '../../strangler'
import type { GameState } from '../state'
import {
  achievementRequirements,
  blueFireUpgradeBase, blueFireUpgradeInitialCosts,
  holyFireUpgradeBase, holyFireUpgradeInitialCosts,
} from '../constants'
import { computeDerived, liveCtx } from '../tick'
import {
  CURRENT_SAVE_VERSION, serializeV4, backupPreV4, seedPrefsFromGame, overlayPrefs, writePrefs, loadFlags,
} from './v4'

// The save pipeline (step 7a): pristine loadGame's STATE half — deserialize,
// version migration, merge, and the gated in-band repairs — extracted in
// original execution order. The DOM replay stays in site script.js and runs
// after this returns; every value it reads has been repaired by then, which
// matches pristine's interleaved order because no replay DOM read precedes
// its repair's pristine position.
//
// Deliberate error-path improvement (user-approved): pristine has NO
// try/catch — a corrupt save throws uncaught, the loading cover never lifts,
// and the 5s autosave then DESTROYS the recoverable blob with a fresh reset.
// Here a failed parse quarantines the raw blob first.

type Loose = GameState & Record<string, any>

export interface StoragePort {
  get(key: string): string | null
  set(key: string, value: string): void
}

export const QUARANTINE_KEY = 'dodecaSaveQuarantine'
export const SAVE_KEY = 'dodecaSave'
export const LAST_SAVED_KEY = 'dodecaLastSaved'

export function browserStorage(): StoragePort {
  const ls = (legacyWindow as any).localStorage
  return { get: (k: string) => ls.getItem(k), set: (k: string, v: string) => ls.setItem(k, v) }
}

export type LoadResult =
  | { ok: true }
  | { ok: false; reason: 'corrupt-json' | 'bad-shape'; quarantined: boolean }

// The raw text of the last successfully-parsed stored save — the v3->v4
// migration backs it up verbatim. Imports go through a different path and
// deliberately never write the backup.
let lastDeserializedRaw: string | null = null

/** Parse + minimal shape check; quarantine the raw blob on failure (once). */
export function deserializeSave(raw: string, storage: StoragePort): { data: any } | LoadResult {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
    lastDeserializedRaw = raw
  } catch {
    return { ok: false, reason: 'corrupt-json', quarantined: quarantine(raw, storage) }
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, reason: 'bad-shape', quarantined: quarantine(raw, storage) }
  }
  return { data: parsed }
}

function quarantine(raw: string, storage: StoragePort): boolean {
  if (storage.get(QUARANTINE_KEY) !== null) return false // never overwrite the first capture
  storage.set(QUARANTINE_KEY, raw)
  return true
}

/**
 * The merge loop, transcribed from pristine script.js:525-545: strings revive
 * to Decimal except dragonName; arrays merge element-wise onto the defaults
 * (default tails survive); the literal string "undefined" skips the key;
 * unknown keys are ADOPTED onto game (legacy-save retention, locked by the
 * round-trip battery).
 */
export function mergeSave(game: Loose, loadgame: Record<string, any>): void {
  const loadKeys = Object.keys(loadgame)
  for (let i = 0; i < loadKeys.length; i++) {
    if (loadgame[loadKeys[i]!] != 'undefined') {
      const thisKey = loadKeys[i]!
      if (typeof loadgame[thisKey] == 'string' && thisKey != 'dragonName') {
        game[thisKey] = new Dec(loadgame[thisKey])
      } else if (Array.isArray(loadgame[thisKey]) && game[thisKey]) {
        for (let j = 0; j < loadgame[thisKey].length; j++) {
          if (typeof loadgame[thisKey][j] == 'string') {
            game[thisKey][j] = new Dec(loadgame[thisKey][j])
          } else {
            game[thisKey][j] = loadgame[thisKey][j]
          }
        }
      } else {
        game[thisKey] = loadgame[thisKey]
      }
    }
  }
}

/** fixSaveVersion (pristine script.js:1266-1290), verbatim semantics. */
export function fixSaveVersion(game: Loose, oldVersion: number | undefined): void {
  if (oldVersion === undefined) {
    if (game.cyanSigilUpgrade1Bought) game.cyanSigilUpgradesBought = [game.cyanSigilUpgrade1Bought, game.cyanSigilUpgrade2Bought, game.cyanSigilUpgrade3Bought, game.cyanSigilUpgrade6Bought]
    if (game.blueSigilUpgrade1Bought) game.blueSigilUpgradesBought = [game.blueSigilUpgrade1Bought, game.blueSigilUpgrade2Bought, game.blueSigilUpgrade3Bought, game.blueSigilUpgrade6Bought]
    if (game.indigoSigilUpgrade1Bought) game.indigoSigilUpgradesBought = [game.indigoSigilUpgrade1Bought, game.indigoSigilUpgrade2Bought, game.indigoSigilUpgrade4Bought, game.indigoSigilUpgrade6Bought]
    if (game.violetSigilUpgrade1Bought) game.violetSigilUpgradesBought = [game.violetSigilUpgrade1Bought, game.violetSigilUpgrade2Bought, game.violetSigilUpgrade5Bought, game.violetSigilUpgrade6Bought]
    if (game.pinkSigilUpgrade3Bought || typeof game.pinkSigilUpgradesBought[0] === 'number') game.pinkSigilUpgradesBought = [new Dec(0), new Dec(0), new Dec(0)]
    const keysToDelete = ['cyanSigilUpgrade1Bought', 'cyanSigilUpgrade2Bought', 'cyanSigilUpgrade3Bought', 'cyanSigilUpgrade4Bought', 'cyanSigilUpgrade5Bought', 'cyanSigilUpgrade6Bought', 'blueSigilUpgrade1Bought', 'blueSigilUpgrade2Bought', 'blueSigilUpgrade3Bought', 'blueSigilUpgrade4Bought', 'blueSigilUpgrade5Bought', 'blueSigilUpgrade6Bought', 'indigoSigilUpgrade1Bought', 'indigoSigilUpgrade2Bought', 'indigoSigilUpgrade3Bought', 'indigoSigilUpgrade4Bought', 'indigoSigilUpgrade5Bought', 'indigoSigilUpgrade6Bought', 'violetSigilUpgrade1Bought', 'violetSigilUpgrade2Bought', 'violetSigilUpgrade3Bought', 'violetSigilUpgrade4Bought', 'violetSigilUpgrade5Bought', 'violetSigilUpgrade6Bought', 'pinkSigilUpgrade3Bought']
    for (let i = 0; i < keysToDelete.length; i++) {
      delete game[keysToDelete[i]!]
    }
    oldVersion = 0
  }
  if (oldVersion < 2) {
    if (game.pinkSigilUpgradesBought[1].gt(0)) {
      game.darkMagicUpgradesBought = game.darkMagicUpgradesBought.slice(0, 10)
    } else {
      game.darkMagicUpgradesBought = game.darkMagicUpgradesBought.slice(0, 8)
    }
  }
  if (oldVersion < 3) {
    if (game.tomeUpgradesBought[11]) game.unlocks = 20
    if (game.tomeUpgradesBought[12]) game.unlocks = 21
  }
}

/**
 * The in-band repairs lifted from the DOM replay WITH their exact gates, in
 * pristine execution order (oracle line refs). Two stay behind in the site
 * replay by design: the dragonTimeEffect recompute (:1186-1196, interleaved
 * with its cap label DOM) and the DOM-as-truth Cyan fallback (:1256).
 * The dead pads (:627-630, :716) are noted, not implemented — the merge
 * already guarantees those lengths (proven in the migration battery).
 */
export function applyInBandRepairs(game: Loose): void {
  // :554 — sigil confirmation toggle healed to true when not a strict boolean
  if (game.confirmations[1] != true && game.confirmations[1] != false) game.confirmations[1] = true
  // :562 — holy-fire desync clamp
  if (!game.holyTetrahedronUpgradesBought[11] && game.unlocks >= 26) game.unlocks = 25
  // :565 — pad unlockedAchievements to the category count (11 -> 25 on load)
  for (let i = 0; i < ACHIEVEMENT_CATEGORY_COUNT(); i++) {
    game.unlockedAchievements[i] = game.unlockedAchievements[i] || 0
  }
  // :568-:569 — transient presentation state force-reset on every load
  game.achievementFlashActive = false
  game.currentTab = 1
  // :592 — add the 4th pink sigil upgrade slot
  if (game.pinkSigilUpgradesBought.length == 3) game.pinkSigilUpgradesBought[3] = new Dec(0)
  // :648 — platinum upgrade 7 clamp without magic upgrade 11 (gate unlocks>=3 block)
  if (game.unlocks >= 3) {
    if (!game.magicUpgradesBought[10] && game.platinumUpgradesBought[6] > 10) game.platinumUpgradesBought[6] = 10
  }
  // :887 — blueFire zero-cost recompute (gate unlocks>=17)
  if (game.unlocks >= 17) {
    for (let i = 1; i < 7; i++) {
      if (game.blueFireUpgradeCosts[i - 1].eq(0)) game.blueFireUpgradeCosts[i - 1] = new Dec(BLUE_FIRE_BASE()[i]!).pow(game.blueFireUpgradesBought[i - 1] as Decimal).mul(BLUE_FIRE_INITIAL()[i]!).floor()
    }
  }
  // :957 — red sigil upgrade 2 legacy price 2500 -> 1500 (gate unlocks>=21)
  if (game.unlocks >= 21) {
    if (game.redSigilUpgrade2Cost.eq(2500)) game.redSigilUpgrade2Cost = new Dec(1500)
  }
  // :1021 — holyFire zero-cost recompute (gate unlocks>=26)
  if (game.unlocks >= 26) {
    for (let i = 1; i < 7; i++) {
      if (game.holyFireUpgradeCosts[i - 1].eq(0)) game.holyFireUpgradeCosts[i - 1] = new Dec(HOLY_FIRE_BASE()[i]!).pow(game.holyFireUpgradesBought[i - 1] as Decimal).mul(HOLY_FIRE_INITIAL()[i]!).floor()
    }
  }
  // :1142 — finality upgrade 3 legacy cost window 1e16 -> 1e15 (gate unlocks>=36)
  if (game.unlocks >= 36) {
    if (game.finalityEssenceUpgradeCosts[2].gt(9e15) && game.finalityEssenceUpgradeCosts[2].lt(1.1e16)) game.finalityEssenceUpgradeCosts[2] = new Dec(1e15)
  }
  // :1200 — old dragon feed cost formula migration (gate dragonStage>=5, NOT unlocks)
  if (game.dragonStage >= 5) {
    if (game.dragonFeedCost.eq(new Dec(10).pow(new Dec(2).pow(game.dragonFood).mul(10)))) game.dragonFeedCost = new Dec(10).pow(new Dec(2).pow(game.dragonFood).mul(8).round())
  }
}

// Balance tables from the engine constants split (step 5) — deep-equal
// drift-guarded against pristine constants.js on every CI run. (The pristine
// tables themselves are script-level consts: lexical globals, NOT window
// properties — unreachable from the bundle by design.)
const ACHIEVEMENT_CATEGORY_COUNT = () => achievementRequirements.length
const BLUE_FIRE_BASE = () => blueFireUpgradeBase
const BLUE_FIRE_INITIAL = () => blueFireUpgradeInitialCosts
const HOLY_FIRE_BASE = () => holyFireUpgradeBase
const HOLY_FIRE_INITIAL = () => holyFireUpgradeInitialCosts

/**
 * The full state pipeline: everything pristine loadGame did before/inside the
 * DOM replay that touches STATE. The replay runs after this returns.
 */
export function loadPipeline(loadgame: Record<string, any>, game: Loose, now: () => number, storage?: StoragePort): void {
  // :516-521 — version snapshot, forward-stamp BEFORE merge (the documented
  // future-save clobber is preserved verbatim, now against v4)
  let shouldCheckVersion = false
  const oldVersion = loadgame.lastMajorChangeVersion
  if (oldVersion === undefined || oldVersion < CURRENT_SAVE_VERSION) {
    shouldCheckVersion = true
  }
  loadgame.lastMajorChangeVersion = CURRENT_SAVE_VERSION

  // v3->v4: one-time verbatim backup of the pre-v4 STORED blob.
  if (storage && (oldVersion === undefined || oldVersion < 4)) {
    backupPreV4(lastDeserializedRaw ?? JSON.stringify(loadgame), storage)
  }

  // flags is v4 envelope metadata, not game state — restore the session flag
  // set and strip it BEFORE the merge (which would otherwise adopt it as an
  // unknown key onto game).
  loadFlags(loadgame)
  delete loadgame.flags

  mergeSave(game, loadgame)
  game.lastUpdate = now() // :546
  if (shouldCheckVersion) fixSaveVersion(game, oldVersion) // :548
  applyInBandRepairs(game)
  if (storage) {
    // prefs seeding must use the MERGED + repaired values (element-wise
    // default tails, the :554 confirmations heal) — never the raw blob
    if (oldVersion === undefined || oldVersion < 4) seedPrefsFromGame(game, storage)
    overlayPrefs(game, storage)
  }

  // v4 blobs carry no derived keys — recompute once so the state is complete
  // before the replay renders and before the first click can read
  // goldPerClick. CONTENT-driven, not version-driven: v3-and-older blobs
  // still carry their values (their load stays byte-identical to 7a — the
  // soak's guarantee), and a future-version blob that carries them is left
  // alone exactly like pristine left future saves alone.
  if (!('goldPerSecond' in loadgame)) {
    // computeDerived also advances the monotone progression keys (best*
    // high-waters, magicScore1-4, magifolds) — a LOAD must never do that, so
    // they are restored afterward; the first real tick raises them exactly
    // as the v3 path did.
    const preserved = PROGRESSION_KEYS.map(k => [k, game[k]] as const)
    computeDerived(game, liveCtx())
    for (const [k, v] of preserved) game[k] = v
  }
}

const PROGRESSION_KEYS = [
  'bestPlatinumToGet', 'bestUraniumToGet', 'bestPlutoniumToGet',
  'magicScore1', 'magicScore2', 'magicScore3', 'magicScore4', 'magifolds',
] as const

/** save(): stamp lastSave, write the v4 blob + the mirrored prefs store. */
export function saveGame(game: Loose, storage: StoragePort, now: () => number): void {
  game.lastSave = now()
  storage.set(SAVE_KEY, serializeSave(game))
  storage.set(LAST_SAVED_KEY, String(game.lastSave))
  writePrefs(game, storage)
}

/** The v4 writer (step 7b). 7a's full-object JSON.stringify lives in git history. */
export function serializeSave(game: Loose): string {
  return serializeV4(game)
}
